import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SelectInput, TextInput } from 'components/Inputs';
import Button from 'components/Button';
import { get, post } from 'api';
import { Levels, MenLevels, WomenLevels } from 'constants/Selections';
import { SuccessToast, ErrorToast } from 'utils/Toasts';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers';
import SpinningBox from 'components/SpinningBox';
import { useTranslation } from 'react-i18next';

const Groups = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const [open, setOpen] = useState({});
  const [tournaments, setTournaments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tournamentGenders, setTournamentGenders] = useState([]);
  const [tournamentLevels, setTournamentLevels] = useState([]);
  const [tournamentsData, setTournamentsData] = useState([]);
  const [form, setForm] = useState({
    tournament: '',
    gender_id: '',
    level_id: '',
    play_until: '',
    isContinued: 1,
  });

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await get(
          '/tournament-registration/tournament-genders',
        );
        const data = await response.json();
        setTournamentGenders(data);
      } catch (error) {
        console.error('Failed to fetch levels', error);
      }
    };

    fetchTypes();
  }, []);

  useEffect(() => {
    const fetchLevels = async () => {
      if (!form.gender_id) return;
      try {
        const response = await get(
          `/tournament-registration/tournament-levels/${form.gender_id}/${form.tournament}`,
        );
        const data = await response.json();
        setTournamentLevels(data);
      } catch (error) {
        console.error('Failed to fetch levels', error);
      }
    };

    fetchLevels();
  }, [form.gender_id, form.tournament]);

  useEffect(() => {
    const getTournaments = async () => {
      const response = await get('/tournaments/is-active');
      const data = await response.json();
      const options = data.map((tournament) => ({
        value: tournament.id,
        label: `${tournament.tournament_name} (${tournament.city})`,
      }));
      setTournaments(options);
      setTournamentsData(data);
    };
    getTournaments();
  }, []);

  const filteredGenders = useMemo(() => {
    return tournamentGenders.filter((type) => {
      const selectedTournament = tournamentsData.find(
        (tournament) => tournament.id === form.tournament,
      );

      if (!selectedTournament) return false;

      if (
        type.id === selectedTournament.men_id &&
        selectedTournament.men_enabled
      ) {
        return true;
      }
      if (
        type.id === selectedTournament.women_id &&
        selectedTournament.women_enabled
      ) {
        return true;
      }
      if (
        type.id === selectedTournament.mix_id &&
        selectedTournament.mix_enabled
      ) {
        return true;
      }

      return false;
    });
  }, [form.tournament, tournamentsData, tournamentGenders]);

  const filteredLevels = useMemo(() => {
    const selectedTournament = tournamentsData.find(
      (tournament) => tournament.id === form.tournament,
    );

    if (!selectedTournament || !form.gender_id) return [];

    // Determine which levels to use based on the selected gender_id
    let genderLevels = [];
    if (form.gender_id === selectedTournament.men_id) {
      genderLevels = selectedTournament.men_levels || [];
    } else if (form.gender_id === selectedTournament.women_id) {
      genderLevels = selectedTournament.women_levels || [];
    } else if (form.gender_id === selectedTournament.mix_id) {
      genderLevels = selectedTournament.mix_levels || [];
    }

    // Filter levels where registration is open
    return genderLevels;
  }, [form.tournament, form.gender_id, tournamentsData]);

  const handleOpen = (id) => {
    setOpen({ ...open, [id]: true });
  };

  const handleClose = (id) => {
    setOpen({ ...open, [id]: false });
  };

  const handleChange = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (
      !form.tournament ||
      !form.gender_id ||
      !form.level_id ||
      !form.play_until
    ) {
      errorToastRef.current.showError('Prašome užpildyti visus laukus');
      setIsSubmitting(false);
      return;
    }
    const body = {
      tournament_id: form.tournament,
      gender_id: form.gender_id,
      level_id: form.level_id,
      play_until: form.play_until,
      isContinued: form.isContinued,
    };
    const response = await post('/groups/scheduleTournament', body);
    const data = await response.json();
    if (data.status === 'SUCCESS') {
      successToastRef.current.showSuccess(data.msg);
      setIsSubmitting(false);
      setForm({
        tournament: '',
        level_id: '',
        gender_id: '',
        play_until: '',
        isContinued: 1,
      });
    } else {
      errorToastRef.current.showError(data.err);
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: '100%', md: '516px' },
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Turnyras*
          </Typography>
          <SelectInput
            name="tournament"
            id="tournament"
            placeholder="Pasirinkite turnyrą"
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.tournament}
            options={tournaments}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Ar normalus tęstinis turnyras?
          </Typography>
          <SelectInput
            name="isContinued"
            id="isContinued"
            placeholder="Ar normalus tęstinis turnyras?"
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.isContinued}
            options={[
              {
                value: 1,
                label: 'Taip',
              },
              {
                value: 0,
                label: 'Ne',
              },
            ]}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('tournamentType')}*
          </Typography>
          <SelectInput
            name="gender_id"
            id="gender_id"
            placeholder={t('selectTournamentType')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.gender_id}
            options={filteredGenders.map((type) => ({
              value: type.id,
              label: t(`${type.name}`),
            }))}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('level')}*
          </Typography>
          <SelectInput
            name="level_id"
            id="level_id"
            placeholder={t('selectLevel')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.level_id}
            options={filteredLevels.map((level) => ({
              value: level.level_id,
              label: t(`${level.level_name}`),
            }))}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Sužaisti iki*
          </Typography>
          <DatePicker
            slotProps={{
              textField: {
                size: 'small',
                inputProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                    display: 'flex',
                  },
                },
                InputLabelProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                  },
                },
              },
            }}
            format="YYYY-MM-DD"
            onChange={(newValue) =>
              setForm({
                ...form,
                play_until: moment(newValue).format('YYYY-MM-DD'),
              })
            }
            value={moment(form.play_until)}
            sx={{
              mt: '6px',
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          maxWidth={{ xs: '100%', md: '100%' }}
          width={'100%'}
          buttontype={'orange'}
          onClick={() => handleSubmit()}
          disabled={isSubmitting}
        >
          {isSubmitting ? <SpinningBox /> : 'Submit'}
        </Button>
      </Box>
    </Box>
  );
};

export default Groups;
